import { useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { Check } from 'lucide-react';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import {
  INFO_DATA,
  InfoFooter,
  SECTION_TITLES,
} from '@/components/v2/cart/RightSection/ReebeloCarePanel';
import ReebeloCareModal from '@/components/v2/cart/RightSection/ReebeloCareModal';
import ShieldIcon from '@/public/icons/reebelocare/cart.svg';
import { priceFormatSuperscript } from '@/lib/price';
import { getPlanMonthlyPrice } from '@/lib/reebelocare/plan';
import settings, { priceFormater } from '@/settings';
import i18n from '@/settings/i18n';

const t = i18n.init('product');
// Plan title and coverage lines are the cart card's, translated there.
const tCart = i18n.init('cart');

/**
 * ReebeloCare on the PDP: the plan MsWarranty offers for the selected offer,
 * as a checkbox card. Ticking it records the choice; Add to Cart then adds the
 * current offer's plan as its own line under the device. Same copy, links and Plan Summary as the
 * cart's card; nothing shows when there's no plan for this offer.
 *
 * Part of the new purchase flow, so behind the same rollout flag.
 */
const ReebeloCare = () => {
  const {
    vendorDetails: { warranty },
    isWarrantySelected,
    setWarrantySelected,
  } = useProductPageContext();
  const { customer } = useCustomerContext();
  const [showPlanSummary, setShowPlanSummary] = useState(false);

  // While a variant change loads the new offer's plan, the last one stays on
  // screen (dimmed, and the box can't be toggled) rather than the card
  // vanishing and reappearing. Its price is only shown, never added: what's
  // added is the current offer's plan, once loaded.
  // Kept in state (adjusted during render, which React supports for the
  // rendering component) rather than a ref, so a render React throws away
  // can't leave behind a plan that never reached the screen.
  const [lastPlan, setLastPlan] = useState(warranty.data);

  if (warranty.data !== lastPlan && (warranty.data || !warranty.loading)) {
    setLastPlan(warranty.data);
  }

  const plan = warranty.data ?? (warranty.loading ? lastPlan : null);

  if (!isPurchaseFlowEnabled(customer?.email)) {
    return null;
  }

  if (!plan) {
    return warranty.loading ? (
      <div className="px-5 lg:px-0" aria-hidden>
        <div className="h-[149.65px] rounded-lg border border-[#D2D3D3] bg-[#F2FFF9] motion-safe:animate-pulse lg:h-[129.75px]" />
      </div>
    ) : null;
  }

  const isSelected = isWarrantySelected;
  const isUpdating = warranty.loading;
  // Per-month price only for plans sold in months (see plan.ts).
  const monthlyPrice = getPlanMonthlyPrice(plan);
  const title = tCart(SECTION_TITLES[settings.store] ?? SECTION_TITLES.default);
  const coverage = INFO_DATA[settings.store] ?? INFO_DATA.default;

  return (
    <div className="px-5 lg:px-0" id="reebelocare">
      {/* The whole card toggles the plan. Its links and buttons (T&Cs, Plan
          Summary) and the checkbox's label are left to them; the checkbox
          stays the keyboard control. */}
      <div
        onClick={(event) => {
          const target = event.target as HTMLElement;

          if (
            isUpdating ||
            target.closest('a, button, input, label, [role="button"]')
          ) {
            return;
          }

          setWarrantySelected(!isSelected);
        }}
        className={classNames(
          // As the prototype: its gradient and shadow, an 8px radius and one
          // grey border whether ticked or not (the checkbox shows which),
          // with no green on hover.
          'rounded-lg border border-[#D2D3D3] bg-[linear-gradient(90deg,_#F2FFF9_0%,_#FFF_100%)] px-3 pb-2.5 pt-3 text-xs leading-tight shadow-[0_1px_2px_rgba(16,_24,_40,_0.06),_0_2px_6px_rgba(14,_78,_50,_0.08)] lg:px-4 lg:py-3.5',
          isUpdating ? 'cursor-wait opacity-60' : 'cursor-pointer',
        )}
      >
        <label
          className={classNames(
            'flex items-center gap-2',
            isUpdating ? 'cursor-wait' : 'cursor-pointer',
          )}
        >
          <input
            id="e2e-pdp-add-reebelocare"
            type="checkbox"
            checked={isSelected}
            disabled={isUpdating}
            onChange={(event) => setWarrantySelected(event.target.checked)}
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-700/40 accent-[#128452] disabled:cursor-wait"
          />
          <Image
            src={ShieldIcon}
            alt=""
            width={20}
            height={20}
            className="h-5 w-5 shrink-0"
          />
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium">{title}</span>
            {/* Desktop only: phones keep the title on its line. */}
            <span className="hidden rounded bg-[#D6FFE2] px-1.5 py-0.5 text-[11px] font-medium text-[#0ca764] lg:inline">
              {t`Recommended`}
            </span>
          </span>
          {/* Below `xs`, the total goes under the per-month price. */}
          <span
            className="flex shrink-0 flex-col items-end gap-y-0.5 whitespace-nowrap text-right xs:flex-row xs:items-baseline xs:gap-x-1"
            translate="no"
          >
            <span
              className={classNames(
                'font-bold',
                monthlyPrice == null && 'invisible',
              )}
              aria-hidden={monthlyPrice == null || undefined}
            >
              {priceFormatSuperscript(
                priceFormater.long(monthlyPrice ?? 0),
                t`/mo`,
              )}
            </span>
            <span className="text-[11px] text-gray-700/60 lg:text-xs">
              ({priceFormatSuperscript(priceFormater.long(plan.price))})
            </span>
          </span>
        </label>
        <div className="my-2.5 h-px w-full bg-[rgba(179,_213,_203,_0.50)]" />
        <ul className="flex flex-col gap-1">
          {coverage.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <Check className="mt-px h-3 w-3 shrink-0" />
              <span>{tCart(item)}</span>
            </li>
          ))}
        </ul>
        <InfoFooter
          isMobile={false}
          neutralLinkHover
          onShowWarrantyDetails={() => setShowPlanSummary(true)}
        />
      </div>
      <ReebeloCareModal
        showModal={showPlanSummary}
        setShowModal={setShowPlanSummary}
      />
    </div>
  );
};

export default ReebeloCare;
